import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, Tag, Share2, ArrowLeft, Facebook, Twitter, Linkedin } from 'lucide-react';
import { SiFacebook, SiX, SiLinkedin } from 'react-icons/si';
import { NEWS_ARTICLES } from '@/data/news';
import { getNewsById, getRelatedNews, formatDate, ROUTE_PATHS } from '@/lib/index';
import { NewsCard } from '@/components/NewsCard';
import { AdBanner } from '@/components/AdBanner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { springPresets } from '@/lib/motion';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  
  if (!id) {
    return <Navigate to={ROUTE_PATHS.NEWS} replace />;
  }

  const article = getNewsById(NEWS_ARTICLES, id);
  
  if (!article) {
    return <Navigate to={ROUTE_PATHS.NEWS} replace />;
  }

  const relatedArticles = getRelatedNews(NEWS_ARTICLES, article.id, article.category, 3);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = article.title;

  const handleShare = (platform: string) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    
    const shareUrls: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
        >
          <Link 
            to={ROUTE_PATHS.NEWS}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to News
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <motion.div 
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
          >
            <Card className="overflow-hidden">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-primary text-primary-foreground">
                    {article.category}
                  </Badge>
                </div>
              </div>

              <CardContent className="p-8">
                <motion.h1 
                  className="text-4xl font-bold mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ ...springPresets.gentle, delay: 0.2 }}
                >
                  {article.title}
                </motion.h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{article.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(article.date)}</span>
                  </div>
                </div>

                <Separator className="mb-6" />

                <div className="prose prose-lg max-w-none mb-8">
                  {article.content.split('\n\n').map((paragraph, index) => (
                    <motion.p
                      key={index}
                      className="mb-4 text-foreground leading-relaxed"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ ...springPresets.gentle, delay: 0.3 + index * 0.05 }}
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>

                <Separator className="mb-6" />

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Tag className="w-4 h-4" />
                    <span>Tags:</span>
                  </div>
                  {article.tags.map((tag) => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Share2 className="w-4 h-4" />
                    <span>Share:</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleShare('facebook')}
                      className="gap-2"
                    >
                      <SiFacebook className="w-4 h-4" />
                      Facebook
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleShare('twitter')}
                      className="gap-2"
                    >
                      <SiX className="w-4 h-4" />
                      Twitter
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleShare('linkedin')}
                      className="gap-2"
                    >
                      <SiLinkedin className="w-4 h-4" />
                      LinkedIn
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.4 }}
            >
              <AdBanner size="leaderboard" position="article-bottom" />
            </motion.div>
          </motion.div>

          <motion.aside
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{article.author}</h3>
                    <p className="text-sm text-muted-foreground">Industry Expert</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Contributing writer specializing in aviation security and technology trends across the Asia-Pacific region.
                </p>
              </CardContent>
            </Card>

            <div>
              <AdBanner size="rectangle" position="sidebar-top" />
            </div>

            {relatedArticles.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    {relatedArticles.map((relatedArticle, index) => (
                      <motion.div
                        key={relatedArticle.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ ...springPresets.gentle, delay: 0.3 + index * 0.1 }}
                      >
                        <Link
                          to={`${ROUTE_PATHS.NEWS}/${relatedArticle.id}`}
                          className="group block"
                        >
                          <div className="flex gap-3">
                            <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                              <img
                                src={relatedArticle.image}
                                alt={relatedArticle.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors mb-1">
                                {relatedArticle.title}
                              </h4>
                              <p className="text-xs text-muted-foreground">
                                {formatDate(relatedArticle.date)}
                              </p>
                            </div>
                          </div>
                        </Link>
                        {index < relatedArticles.length - 1 && (
                          <Separator className="mt-4" />
                        )}
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div>
              <AdBanner size="rectangle" position="sidebar-bottom" />
            </div>

            <Card className="bg-primary text-primary-foreground">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Stay Updated</h3>
                <p className="text-sm mb-4 opacity-90">
                  Subscribe to our newsletter for the latest industry news and insights.
                </p>
                <Button 
                  variant="secondary" 
                  className="w-full"
                  asChild
                >
                  <Link to={ROUTE_PATHS.CONTACT}>Subscribe Now</Link>
                </Button>
              </CardContent>
            </Card>
          </motion.aside>
        </div>
      </div>
    </div>
  );
}
